import React, { useState } from 'react';
import './AddBatch.css';
import { API_BASE } from '../config';
import { authFetch } from '../utils/api';

const AddBatch = ({ onBack, onSave }) => {
    const [formData, setFormData] = useState({
        batch_name: '',
        start_year: new Date().getFullYear(),
        end_year: new Date().getFullYear() + 1,
        type: '',
        subjects: []
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [customSubject, setCustomSubject] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'start_year' || name === 'end_year' ? parseInt(value) : value
        }));
    };

    const handleAddSubject = (e) => {
        const selectedSubject = e.target.value;
        if (selectedSubject && !formData.subjects.includes(selectedSubject)) {
            setFormData(prev => ({
                ...prev,
                subjects: [...prev.subjects, selectedSubject]
            }));
        }
    };

    const handleRemoveSubject = (subjectToRemove) => {
        setFormData(prev => ({
            ...prev,
            subjects: prev.subjects.filter(subject => subject !== subjectToRemove)
        }));
    };

    const handleAddCustomSubject = () => {
        const trimmed = customSubject.trim();
        if (trimmed && !formData.subjects.some(s => s.toLowerCase() === trimmed.toLowerCase())) {
            setFormData(prev => ({
                ...prev,
                subjects: [...prev.subjects, trimmed]
            }));
            setCustomSubject('');
        }
    };

    const handleCustomSubjectKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddCustomSubject();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!formData.batch_name.trim()) {
            setError('Batch name is required');
            return;
        }

        if (!formData.type) {
            setError('Batch type is required');
            return;
        }

        if (formData.end_year <= formData.start_year) {
            setError('End year must be greater than start year');
            return;
        }

        if (formData.subjects.length === 0) {
            setError('At least one subject is required');
            return;
        }

        setLoading(true);
        
        try {
            const response = await authFetch(`${API_BASE}/api/batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create batch');
            }

            const result = await response.json();
            onSave(result.batch);
            onBack();
        } catch (err) {
            setError(err.message || 'Failed to create batch');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-batch">
            <div className="add-batch-header">
                <button className="back-button" onClick={onBack}>← Back</button>
                <h2>Add New Batch</h2>
            </div>

            <form onSubmit={handleSubmit} className="add-batch-form">
                {error && (
                    <div className="error-message" style={{ 
                        padding: '10px', 
                        backgroundColor: '#fee', 
                        color: '#c00', 
                        borderRadius: '4px', 
                        marginBottom: '20px' 
                    }}>
                        {error}
                    </div>
                )}

                <div className="form-section">
                    <h3>Batch Details</h3>
                    
                    <div className="form-group">
                        <label>Batch Name *</label>
                        <input
                            type="text"
                            name="batch_name"
                            value={formData.batch_name}
                            onChange={handleChange}
                            placeholder="e.g., NEET 2025 Batch A"
                            required
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Start Year *</label>
                            <input
                                type="number"
                                name="start_year"
                                value={formData.start_year}
                                onChange={handleChange}
                                min="2020"
                                max="2050"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>End Year *</label>
                            <input
                                type="number"
                                name="end_year"
                                value={formData.end_year}
                                onChange={handleChange}
                                min="2020"
                                max="2050"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Batch Type *</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Type</option>
                            <option value="NEET">NEET</option>
                            <option value="JEE">JEE</option>
                            <option value="Foundation">Foundation (6th-10th)</option>
                            <option value="Board">Board</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Subjects *</label>
                        <select
                            onChange={handleAddSubject}
                            value=""
                            style={{ marginBottom: '10px' }}
                        >
                            <option value="" disabled>Select a subject</option>
                            <option value="Physics">Physics</option>
                            <option value="Chemistry">Chemistry</option>
                            <option value="Biology">Biology</option>
                            <option value="Mathematics">Mathematics</option>
                        </select>

                        {/* Custom subject input */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                            <input
                                type="text"
                                value={customSubject}
                                onChange={(e) => setCustomSubject(e.target.value)}
                                onKeyDown={handleCustomSubjectKeyDown}
                                placeholder="Or type a custom subject..."
                                style={{
                                    flex: 1,
                                    padding: '10px 12px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '14px'
                                }}
                            />
                            <button
                                type="button"
                                onClick={handleAddCustomSubject}
                                disabled={!customSubject.trim()}
                                style={{
                                    padding: '10px 16px',
                                    background: customSubject.trim()
                                        ? 'linear-gradient(135deg, #5b5fc7 0%, #4347a0 100%)'
                                        : '#cbd5e0',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: customSubject.trim() ? 'pointer' : 'not-allowed',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                + Add
                            </button>
                        </div>

                        <div className="subjects-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {formData.subjects.map((subject, index) => (
                                <span
                                    key={index}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    {subject}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSubject(subject)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#666',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            padding: '0'
                                        }}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={onBack} disabled={loading}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-submit" disabled={loading}>
                        {loading ? 'Creating...' : 'Create Batch'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddBatch;
